import React, { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import Reveal from './Reveal';

const fmtPrice = (price, currency) => {
  if (!price) return 'مجاناً';
  try {
    return new Intl.NumberFormat('ar-SA', { style: 'currency', currency: currency || 'SAR', maximumFractionDigits: 0 }).format(price);
  } catch {
    return `${price} ${currency || 'ر.س'}`;
  }
};

export const LandingPricing = ({ landing, plans, navigate }) => {
  const [cycle, setCycle] = useState('monthly');

  return (
    <section id="pricing" className="mx-auto max-w-7xl px-5 py-24 md:py-32" data-testid="landing-pricing">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-sm font-bold uppercase tracking-wider text-teal-400">الباقات</span>
          <h2 className="font-display mt-3 text-2xl font-bold text-white sm:text-3xl lg:text-4xl" data-testid="landing-pricing-title">
            {landing?.pricing_title || 'باقة تناسب حجم منشأتك'}
          </h2>
          <p className="mt-3 text-slate-400" data-testid="landing-pricing-subtitle">
            {landing?.pricing_subtitle || 'اختر الباقة المناسبة، ويمكنك الترقية في أي وقت من لوحة التحكم'}
          </p>

          <div className="mt-7 inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1" data-testid="landing-pricing-cycle-toggle">
            <button
              onClick={() => setCycle('monthly')}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${cycle === 'monthly' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="landing-pricing-cycle-monthly"
            >
              شهري
            </button>
            <button
              onClick={() => setCycle('annual')}
              className={`flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-bold transition-colors ${cycle === 'annual' ? 'bg-teal-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid="landing-pricing-cycle-annual"
            >
              سنوي
              <span className="rounded-full bg-orange-500/20 px-2 py-0.5 text-[10px] font-bold text-orange-300">وفّر أكثر</span>
            </button>
          </div>
        </div>
      </Reveal>

      {!plans ? (
        <div className="mt-12 flex flex-wrap justify-center gap-6">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-96 w-full max-w-sm animate-pulse rounded-2xl bg-white/[0.03] sm:w-[calc(50%-12px)] lg:w-[280px]" />)}
        </div>
      ) : plans.length === 0 ? (
        <p className="mt-10 text-center text-sm text-slate-400" data-testid="landing-pricing-empty">لم يتم تحديد باقات بعد</p>
      ) : (
        <div className="mt-12 flex flex-wrap justify-center gap-6">
          {plans.map((p) => {
            const isFree = !p.price;
            const originalAnnual = p.price * 12;
            const showAnnualDiscount = !isFree && cycle === 'annual' && p.annual_price_effective < originalAnnual;
            return (
              <Reveal key={p.plan_id}>
                <div
                  className={`flex h-full w-full max-w-sm flex-col rounded-2xl border p-7 backdrop-blur-md sm:w-[calc(50%-12px)] lg:w-[280px] ${p.badge_text ? 'border-teal-500/60 bg-white/[0.04] shadow-2xl shadow-teal-500/10' : 'border-white/10 bg-white/[0.02]'}`}
                  data-testid={`landing-plan-${p.plan_id}`}
                >
                  {p.badge_text && (
                    <span className="mb-4 inline-flex w-fit items-center gap-1 rounded-full bg-teal-500/10 px-3 py-1 text-xs font-bold text-teal-400" data-testid={`landing-plan-badge-${p.plan_id}`}>
                      <Sparkles className="h-3.5 w-3.5" /> {p.badge_text}
                    </span>
                  )}
                  <h3 className="font-display text-lg font-bold text-white">{p.display_name}</h3>
                  <p className="mt-1 text-sm text-slate-400">{p.tagline}</p>
                  <div className="mt-5">
                    {isFree ? (
                      <span className="font-display text-3xl font-black text-white" data-testid={`landing-plan-price-${p.plan_id}`}>مجاناً</span>
                    ) : cycle === 'monthly' ? (
                      <>
                        <span className="font-display text-3xl font-black text-white" data-testid={`landing-plan-price-${p.plan_id}`}>{fmtPrice(p.price, p.currency)}</span>
                        <span className="ms-1 text-sm text-slate-400">/ شهرياً</span>
                      </>
                    ) : (
                      <div className="flex flex-wrap items-baseline gap-2">
                        {showAnnualDiscount && (
                          <span className="text-base font-bold text-slate-500 line-through decoration-2 decoration-red-500" data-testid={`landing-plan-price-original-${p.plan_id}`}>
                            {fmtPrice(originalAnnual, p.currency)}
                          </span>
                        )}
                        <span className="font-display text-3xl font-black text-white" data-testid={`landing-plan-price-${p.plan_id}`}>{fmtPrice(p.annual_price_effective, p.currency)}</span>
                        <span className="text-sm text-slate-400">/ سنوياً</span>
                      </div>
                    )}
                  </div>
                  {isFree ? (
                    <div className="text-xs text-slate-500">{p.billing_period}</div>
                  ) : cycle === 'annual' ? (
                    p.annual_savings_percent > 0 && (
                      <div className="text-xs font-bold text-teal-400" data-testid={`landing-plan-annual-note-${p.plan_id}`}>وفر {p.annual_savings_percent}% سنوياً</div>
                    )
                  ) : (
                    <div className="text-xs text-slate-500">فوترة شهرية</div>
                  )}

                  <ul className="mt-6 flex-1 space-y-3">
                    {(p.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal-400" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => navigate('/login')}
                    className={`mt-7 rounded-full py-3 text-sm font-bold transition-colors ${p.badge_text ? 'bg-teal-600 text-white shadow-[0_0_15px_rgba(13,148,136,0.4)] hover:bg-teal-500' : 'border border-white/20 text-white hover:bg-white/10'}`}
                    data-testid={`landing-plan-cta-${p.plan_id}`}
                  >
                    {p.cta_text || 'ابدأ الآن'}
                  </button>
                </div>
              </Reveal>
            );
          })}
          <Reveal>
            <div
              className="flex w-full max-w-sm flex-col justify-start rounded-2xl border border-dashed border-white/15 bg-white/[0.015] p-7 sm:w-[calc(50%-12px)] lg:w-[280px]"
              data-testid="landing-plan-custom"
            >
              <div>
                <h3 className="font-display text-lg font-bold text-white">تبغى باقة أكبر؟</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  منشآت كبيرة أو فروع متعددة أو احتياج خاص؟ نرتّب لك باقة مخصصة بعدد لاعبين ومزايا تناسبك.
                </p>
              </div>
              <button
                onClick={() => navigate('/contact')}
                className="mt-7 rounded-full border border-white/20 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10"
                data-testid="landing-plan-custom-cta"
              >
                تواصل معنا
              </button>
            </div>
          </Reveal>
        </div>
      )}
    </section>
  );
};

export default LandingPricing;
